import { Api } from '@/Api';
import { DEBUG_NAMESPACE, useDebug } from '@/composables/useDebug';
import { defineStore } from 'pinia';

const ITEMS_PER_PAGE = 20;

const { debug } = useDebug(DEBUG_NAMESPACE.STORE);

export interface CollectionItemInterface {
  id: string;
  name: string;
  hasSecurity: boolean;
  security?: {
    sharing: string;
    password: string;
    passwordProtected: boolean;
    expires: number;
    expiresAfterViews: number;
    expiresAt: string;
    expired: boolean;
    specificUsers: string[];
  };
  onboarding: boolean;
  author: string;
  shareUrl: string;
  //itemsCount: number; use items.length instead
  viewCount: number;
  createdAt: Date;
  updatedAt: Date;
  items: string[];
}

export interface CollectionState {
  currentPage: number;
  totalCount: number;
  isLoading: boolean;
  isSaving: boolean;
  toggleSaveState: string[]; // store currently saving state for each item in collection
  list: CollectionItemInterface[];
}

export const useCollectionsStore = defineStore('collectionsStore', {
  state: (): CollectionState => ({
    isLoading: false,
    isSaving: false,
    currentPage: 1,
    totalCount: 0,
    toggleSaveState: [],
    list: [],
  }),
  actions: {
    parseList(data: any) {
      return data.map((el: any) => {
        return {
          id: el.id,
          ...el.attributes,
          createdAt: el.attributes.createdAt ? new Date(el.attributes.createdAt) : undefined,
          updatedAt: el.attributes.updatedAt ? new Date(el.attributes.updatedAt) : undefined,
          items: el.relationships.items.data.map((item: any) => item.id),
          author: el.relationships.user.meta.email,
        };
      });
    },
    async load() {
      try {
        this.isLoading = true;
        this.$patch({ currentPage: 1, list: [], totalCount: 0 });

        const res = await Api.collections.list(this.currentPage, ITEMS_PER_PAGE);

        if (!res.data.collections || res.data.collections.length === 0) {
          this.isLoading = false;
          return Promise.resolve();
        }

        this.$patch({
          list: this.parseList(res.data.collections),
          totalCount: res.data.totalCount,
        });

        this.isLoading = false;
        return Promise.resolve();
      } catch (e) {
        debug(e);
        this.isLoading = false; // Fix: Set loading to false on error
        return Promise.reject('Cannot load collections list');
      }
    },
    async loadMore() {
      if (this.isLoadedAll || this.isLoading) {
        return Promise.resolve();
      }

      try {
        this.$patch({ currentPage: this.currentPage + 1 });
        this.isLoading = true;

        const res = await Api.collections.list(this.currentPage, ITEMS_PER_PAGE);

        this.$patch({
          list: [...this.list, ...this.parseList(res.data.collections)],
          totalCount: res.data.totalCount,
        });

        this.isLoading = false;
        return Promise.resolve();
      } catch (e) {
        debug(e);
        this.isLoading = false;
        return Promise.reject('Cannot load more collections');
      }
    },
    async create(name: string, itemId: string) {
      this.isSaving = true;

      return Api.collections.create(name).then(res => {
        this.isSaving = false;

        if (!res.data.collection) {
          return Promise.resolve();
        }

        this.$patch({
          list: [...this.parseList([res.data.collection]), ...this.list],
          totalCount: this.totalCount + 1,
        });
        return this.addItem(itemId, res.data.collection.id);
      });
    },
    async toggleItem(itemId: string, collectionId: string) {
      if (this.toggleSaveState.includes(collectionId)) {
        return Promise.resolve();
      }

      const collection = this.list.find(el => el.id === collectionId);

      if (!collection) {
        return Promise.reject('Collection not found');
      }

      if (collection.items.includes(itemId)) {
        return this.removeItem(itemId, collectionId);
      }

      return this.addItem(itemId, collectionId);
    },
    async addItem(itemId: string, collectionId: string) {
      this.toggleSaveState.push(collectionId);

      return Api.collections.addItem(itemId, collectionId).then(() => {
        this.toggleSaveState = this.toggleSaveState.filter(el => el !== collectionId);

        return this.$patch({
          list: this.list.map(el => {
            if (el.id === collectionId) {
              return {
                ...el,
                items: [...el.items, itemId],
              };
            }

            return el;
          }),
        });
      });
    },
    async removeItem(itemId: string, collectionId: string) {
      this.toggleSaveState.push(collectionId);

      return Api.collections.removeItem(itemId, collectionId).then(() => {
        this.toggleSaveState = this.toggleSaveState.filter(el => el !== collectionId);

        return this.$patch({
          list: this.list.map(el => {
            if (el.id === collectionId) {
              return {
                ...el,
                items: el.items.filter(el => el !== itemId),
              };
            }

            return el;
          }),
        });
      });
    },
    reset() {},
  },
  getters: {
    isLoadedAll: (state: CollectionState) => state.currentPage * ITEMS_PER_PAGE >= state.totalCount,
    filteredList: (state: CollectionState) =>
      state.list.filter((col: CollectionItemInterface) => {
        if (col.onboarding) {
          return false;
        }

        // only if organization can edit collection show it
        if (col.hasSecurity && col.security && !['oe', 'un'].includes(col.security.sharing)) {
          return false;
        }

        return true;
      }),

    // Get filtered collections for picker component
    getPickerCollections: (state: CollectionState) => (options?: { excludeSecure?: boolean; excludeOnboarding?: boolean }) => {
      let filtered = state.list;

      const shouldExcludeOnboarding = options?.excludeOnboarding ?? true;
      const shouldExcludeSecure = options?.excludeSecure ?? true;

      if (shouldExcludeOnboarding) {
        filtered = filtered.filter(c => !c.onboarding);
      }

      if (shouldExcludeSecure) {
        filtered = filtered.filter(c => !c.hasSecurity);
      }

      return filtered;
    },

    // Get collection by ID
    getCollectionById: (state: CollectionState) => (id: string) => {
      return state.list.find(c => c.id === id);
    },

    // Check if collection contains item
    collectionContainsItem: (state: CollectionState) => (collectionId: string, itemId: string) => {
      const collection = state.list.find(c => c.id === collectionId);
      return collection?.items.includes(itemId) ?? false;
    },

    // Check if specific collection is being saved
    isCollectionSaving: (state: CollectionState) => (collectionId: string) => {
      return state.toggleSaveState.includes(collectionId);
    },

    // Get collection name by ID
    getCollectionName: (state: CollectionState) => (collectionId: string) => {
      const collection = state.list.find(c => c.id === collectionId);
      return collection?.name || 'Unknown Collection';
    },
  },
});
